// @ts-nocheck
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { CheckCircle2, Upload, Loader2 } from 'lucide-react';
import { deploymentsApi, type ValidationResult } from '../../../api/deploymentsApi';
import { useValidateDeploymentCSV } from '../../../hooks/useValidateDeploymentCSV';
import { useImportDeploymentItems } from '../../../hooks/useImportDeploymentItems';
import { downloadBlob } from '../../../utils/downloadBlob';

interface DeploymentCSVImportDialogProps {
  deploymentId: string;
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

type Step = 'upload' | 'validating' | 'preview' | 'importing' | 'complete';


export const DeploymentCSVImportDialog = ({
  deploymentId,
  open,
  onClose,
  onImported,
}: DeploymentCSVImportDialogProps) => {
  const [step, setStep] = useState<Step>('upload');
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [inserted, setInserted] = useState(0);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const handleDownloadTemplate = async () => {
    setTemplateError(null);
    setDownloadingTemplate(true);
    try {
      const blob = await deploymentsApi.downloadCsvTemplate(deploymentId, 30);
      downloadBlob(blob, 'deployment-template.csv');
    } catch (err: any) {
      setTemplateError(err?.message ?? 'Erreur lors du téléchargement du template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const { mutate: validateCSV, error: validateError } = useValidateDeploymentCSV(deploymentId, {
    onSuccess: (result) => {
      setValidation(result);
      setStep('preview');
    },
    onError: () => setStep('upload'),
  });

  const { mutate: importItems, error: importError } = useImportDeploymentItems(deploymentId, {
    onSuccess: (result) => {
      setInserted(result.inserted);
      setStep('complete');
      onImported();
    },
    onError: () => setStep('preview'),
  });

  const reset = () => {
    setStep('upload');
    setValidation(null);
    setInserted(0);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (file: File) => {
    setStep('validating');
    validateCSV(file);
  };

  const handleImport = () => {
    if (!validation?.validItems.length) return;
    setStep('importing');
    importItems(validation.validItems);
  };

  const renderUpload = () => (
    <div className="space-y-4">
      <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors">
        <Upload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
        <p className="text-sm text-gray-600 mb-3">Glisser-déposer un fichier CSV ou</p>
        <label className="inline-block">
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
            }}
          />
          <Button type="button" variant="outline" asChild>
            <span>Parcourir</span>
          </Button>
        </label>
      </div>
      <Button variant="link" size="sm" onClick={handleDownloadTemplate} disabled={downloadingTemplate}>
        {downloadingTemplate && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
        Télécharger le template CSV
      </Button>
      {templateError && <p className="text-sm text-red-600">{templateError}</p>}
      {validateError && <p className="text-sm text-red-600">{(validateError as Error).message}</p>}
    </div>
  );

  const renderValidating = () => (
    <div className="py-8 text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
      <p className="text-sm text-gray-600">Lecture du CSV…</p>
      <p className="text-xs text-gray-500 mt-1">
        Les magasins, mobiliers et marques seront vérifiés au moment de la publication.
      </p>
    </div>
  );

  const renderPreview = () => {
    if (!validation) return null;
    const { errors, warnings, validItems, totalRows } = validation;
    return (
      <div className="space-y-4 min-w-0 max-w-full">
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="bg-green-50 border border-green-200 rounded p-3">
            <p className="text-green-700 font-medium">{validItems.length} valides</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <p className="text-yellow-700 font-medium">{warnings.length} avertissements</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-red-700 font-medium">{errors.length} erreurs</p>
          </div>
        </div>

        <p className="text-xs text-gray-500">Total lignes traitées : {totalRows}</p>

        {errors.length > 0 && (
          <div className="max-h-40 overflow-y-auto border rounded p-3 bg-red-50">
            <p className="text-xs font-medium text-red-700 mb-2">Erreurs</p>
            <ul className="space-y-1 text-xs text-red-600">
              {errors.slice(0, 20).map((e, i) => (
                <li key={i}>
                  Ligne {e.line}{e.column ? ` — ${e.column}` : ''} : {e.message}
                </li>
              ))}
              {errors.length > 20 && <li>… et {errors.length - 20} autres</li>}
            </ul>
          </div>
        )}

        {validItems.length > 0 && (
          <div className="border rounded overflow-hidden min-w-0 max-w-full">
            <div className="text-xs font-medium bg-gray-50 px-3 py-2 border-b">Aperçu (5 premières lignes)</div>
            <div className="overflow-x-auto max-w-full">
              <table className="w-full text-xs whitespace-nowrap">
                <thead>
                  <tr className="bg-gray-50 text-gray-600">
                    <th className="px-2 py-1 text-left">Secteur</th>
                    <th className="px-2 py-1 text-left">Enseigne</th>
                    <th className="px-2 py-1 text-left">PDV</th>
                    <th className="px-2 py-1 text-left">Marque</th>
                    <th className="px-2 py-1 text-left">Mobilier</th>
                    <th className="px-2 py-1 text-left">Technicien</th>
                    <th className="px-2 py-1 text-left">Email</th>
                    <th className="px-2 py-1 text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {validItems.slice(0, 5).map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-2 py-1">{item.sector}</td>
                      <td className="px-2 py-1">{item.shop_sign}</td>
                      <td className="px-2 py-1">{item.store_name}</td>
                      <td className="px-2 py-1">{item.brand}</td>
                      <td className="px-2 py-1">{item.furniture_name}</td>
                      <td className="px-2 py-1">{item.technician_name}</td>
                      <td className="px-2 py-1">{item.technician_email}</td>
                      <td className="px-2 py-1">{item.scheduled_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {importError && <p className="text-sm text-red-600">{(importError as Error).message}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={reset}>Recommencer</Button>
          <Button onClick={handleImport} disabled={validItems.length === 0}>
            Importer {validItems.length} items
          </Button>
        </div>
      </div>
    );
  };

  const renderImporting = () => (
    <div className="py-8 text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
      <p className="text-sm text-gray-600">Sauvegarde en cours…</p>
    </div>
  );

  const renderComplete = () => (
    <div className="py-6 text-center space-y-3">
      <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
      <p className="text-sm text-gray-700">{inserted} item(s) importé(s)</p>
      <Button onClick={handleClose}>Fermer</Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Importer un CSV</DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Télécharger le CSV à importer dans ce déploiement'}
            {step === 'validating' && 'Lecture du CSV (format uniquement)'}
            {step === 'preview' && 'Vérifier et confirmer l’import'}
            {step === 'importing' && 'Import en base'}
            {step === 'complete' && 'Terminé'}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && renderUpload()}
        {step === 'validating' && renderValidating()}
        {step === 'preview' && renderPreview()}
        {step === 'importing' && renderImporting()}
        {step === 'complete' && renderComplete()}
      </DialogContent>
    </Dialog>
  );
};
